import React, { useEffect, useState } from 'react';
import { authService } from '../services/authService';
import { clearOnboardingPending, isOnboardingPending, onOnboardingRequested } from '../utils/onboarding';
import { inviteService } from '../services/inviteService';
import { clearStoredReferrer, getStoredReferrer } from '../utils/invite';
import OnboardingWizard from './OnboardingWizard';
import PostComposer from './PostComposer';
import { useNotification } from './NotificationContext';

/**
 * Shows the welcome flow once, to an account that just registered. Waits for the legal
 * disclaimer to be dealt with first so two dialogs never stack.
 */
const OnboardingGate: React.FC<{ disclaimerPending: boolean | null }> = ({ disclaimerPending }) => {
  const { showSuccess } = useNotification();
  const [userId, setUserId] = useState(() => authService.getUser()?.userId ?? null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);

  useEffect(() => authService.subscribe(() => setUserId(authService.getUser()?.userId ?? null)), []);

  // A visitor who arrived through an /invite link and has now signed in: tell the server who
  // invited them (it ignores anyone but a brand-new account), then drop the stored referrer.
  useEffect(() => {
    if (!userId) return;
    const referrer = getStoredReferrer();
    if (!referrer) return;
    clearStoredReferrer();
    inviteService.redeem(referrer).catch(() => { /* attribution is best-effort */ });
  }, [userId]);

  useEffect(() => {
    if (disclaimerPending === false && userId && isOnboardingPending(userId)) setWizardOpen(true);
  }, [disclaimerPending, userId]);

  useEffect(() => onOnboardingRequested(() => setWizardOpen(true)), []);

  if (!userId) return null;

  const handleFinish = (writeFirstPost: boolean) => {
    clearOnboardingPending();
    setWizardOpen(false);
    if (writeFirstPost) setComposerOpen(true);
  };

  return (
    <>
      {wizardOpen && <OnboardingWizard open onFinish={handleFinish} />}
      {/* Mounted only when needed — the composer loads buddies/groups/clubs on mount. */}
      {composerOpen && (
        <PostComposer
          open
          onClose={() => setComposerOpen(false)}
          onCreated={() => showSuccess('Welcome to the community — your first post is live!')}
        />
      )}
    </>
  );
};

export default OnboardingGate;
